/**
 * Panneau latéral du plan des pistes : « Où peut-on aller » par niveau,
 * index des pistes et des remontées, défauts du réseau à corriger.
 */
import { TriangleAlert, Info } from 'lucide-preact';
import { DIFFICULTY_LABEL, LIFTS, SKILL_LEVELS, type NetworkIssue } from '@snowchain/shared';
import { LIFT_STATUS_LABEL, type LiftShape, type PisteShape } from './overlay';
import { lengthLabel, PISTE_COLOR, WORKS_COLOR } from './style';
import type { HoverKind } from './MapLayer';

export type Skill = 0 | 1 | 2 | null;

interface Props {
  pistes: PisteShape[];
  lifts: LiftShape[];
  issues: NetworkIssue[];
  skill: Skill;
  summary: string;
  hotPiste: number | null;
  hotLift: number | null;
  /** Pistes et remontées accessibles au niveau choisi (null : tout). */
  reachPistes: Set<number> | null;
  reachLifts: Set<number> | null;
  onSkill: (s: Skill) => void;
  onHover: (kind: HoverKind | null, id: number) => void;
  onPick: (kind: 'piste' | 'lift', id: number) => void;
}

function pisteState(p: PisteShape): { label: string; cls: string } {
  if (p.works) return { label: 'En travaux', cls: 'is-works' };
  if (!p.open) return { label: 'Fermée', cls: 'is-closed' };
  return { label: 'Ouverte', cls: 'is-open' };
}

export function Side(props: Props) {
  const { pistes, lifts, issues, skill, summary } = props;
  const sorted = [...pistes].sort((a, b) => a.num - b.num);
  const chips: { s: Skill; label: string }[] = [{ s: null, label: 'Tous' }, ...SKILL_LEVELS.map((l) => ({ s: l.skill as Skill, label: l.label }))];
  return (
    <aside class="tm-side">
      <section class="tm-s-block">
        <h3 class="tm-s-title">Où peut-on aller ?</h3>
        <div class="tm-chips" role="radiogroup" aria-label="Niveau des skieurs">
          {chips.map((c) => (
            <button key={String(c.s)} type="button" role="radio" aria-checked={skill === c.s} class={`tm-chip${skill === c.s ? ' is-on' : ''}`} onClick={() => props.onSkill(c.s)}>
              {c.label}
            </button>
          ))}
        </div>
        <p class="tm-s-summary">{summary}</p>
      </section>
      <section class="tm-s-block tm-s-scroll">
        <h3 class="tm-s-title">Pistes</h3>
        {sorted.length === 0 && <p class="tm-s-empty">Aucune piste tracée.</p>}
        <ul class="tm-index">
          {sorted.map((p) => {
            const st = pisteState(p);
            const off = props.reachPistes && !props.reachPistes.has(p.id);
            return (
              <li key={p.id}>
                <button
                  type="button"
                  class={`tm-i-row${props.hotPiste === p.id ? ' is-hot' : ''}${off ? ' is-off' : ''}`}
                  onMouseEnter={() => props.onHover('piste', p.id)}
                  onMouseLeave={() => props.onHover(null, p.id)}
                  onFocus={() => props.onHover('piste', p.id)}
                  onBlur={() => props.onHover(null, p.id)}
                  onClick={() => props.onPick('piste', p.id)}
                  title={`Piste ${DIFFICULTY_LABEL[p.difficulty].toLowerCase()}`}
                >
                  <span class="tm-i-num" style={{ background: p.works ? WORKS_COLOR : PISTE_COLOR[p.difficulty] }}>
                    {p.num}
                  </span>
                  <span class="tm-i-name">{p.name}</span>
                  <span class="tm-i-len">{lengthLabel(p.length)}</span>
                  <span class={`tm-i-state ${st.cls}`}>{st.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
        <h3 class="tm-s-title">Remontées</h3>
        {lifts.length === 0 && <p class="tm-s-empty">Aucune remontée.</p>}
        <ul class="tm-index">
          {lifts.map((l) => {
            const off = props.reachLifts && !props.reachLifts.has(l.id);
            const cls = l.status === 'open' ? 'is-open' : l.status === 'construction' ? 'is-works' : 'is-closed';
            return (
              <li key={l.id}>
                <button
                  type="button"
                  class={`tm-i-row${props.hotLift === l.id ? ' is-hot' : ''}${off ? ' is-off' : ''}`}
                  onMouseEnter={() => props.onHover('lift', l.id)}
                  onMouseLeave={() => props.onHover(null, l.id)}
                  onFocus={() => props.onHover('lift', l.id)}
                  onBlur={() => props.onHover(null, l.id)}
                  onClick={() => props.onPick('lift', l.id)}
                >
                  <span class={`tm-i-lift tm-k-${l.kind}`} aria-hidden="true" />
                  <span class="tm-i-name">
                    {l.name}
                    <small>{LIFTS[l.kind].label}</small>
                  </span>
                  <span class={`tm-i-state ${cls}`}>{LIFT_STATUS_LABEL[l.status]}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>
      {issues.length > 0 && (
        <section class="tm-s-block tm-s-issues">
          <h3 class="tm-s-title">À corriger</h3>
          <ul class="tm-issues">
            {issues.map((it, k) => {
              const target = it.pisteId !== undefined ? ({ kind: 'piste', id: it.pisteId } as const) : it.liftId !== undefined ? ({ kind: 'lift', id: it.liftId } as const) : null;
              return (
                <li key={k}>
                  <button type="button" class={`tm-issue is-${it.level}`} disabled={!target} onClick={() => target && props.onPick(target.kind, target.id)} title={it.message}>
                    {it.level === 'warn' ? <TriangleAlert size={14} /> : <Info size={14} />}
                    <span>
                      <strong>{it.title}</strong>
                      <small>{it.message}</small>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </aside>
  );
}
